// Notification utility for testing and managing local notifications and push notifications
import { LocalNotifications } from "@capacitor/local-notifications";
import { Capacitor } from "@capacitor/core";
import { PushNotificationManager } from "./pushNotificationManager";

import { crashlytics } from "../services/firebase";
export class NotificationManager {
  static async initialize(userId?: string) {
    const results = {
      local: false,
      push: false,
    };

    if (!Capacitor.isNativePlatform()) {
      return results;
    }

    // Initialize Local Notifications
    results.local = await this.initializeLocalNotifications();

    // Initialize Push Notifications
    results.push = await PushNotificationManager.initialize(userId);

    return results;
  }

  private static async initializeLocalNotifications(): Promise<boolean> {
    try {
      // Check if LocalNotifications is available
      if (!LocalNotifications) {
        crashlytics.logError(
          "LocalNotifications plugin not available",
          "notificationManager.ts",
          new Error("LocalNotifications plugin not available"),
        );
        return false;
      }

      // Request notification permissions
      const permResult = await LocalNotifications.requestPermissions();

      if (permResult.display === "granted") {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      crashlytics.logError(
        `Error in initializeLocalNotifications: ${JSON.stringify(error?.message)}`,
        "notificationManager.ts",
        error,
      );
      return false;
    }
  }

  static async scheduleNotificationForActivity(
    activityTitle: string,
    activityId: string,
    type: "local" | "push" | "both" = "both",
    delayMinutes: number = 0,
  ) {
    if (!Capacitor.isNativePlatform()) return;

    const notificationData = {
      title: "New Activity Available!",
      body: `${activityTitle} - Join now!`,
      data: {
        type: "activity" as const,
        activityId: activityId,
      },
    };

    try {
      if (type === "local" || type === "both") {
        await this.scheduleLocalActivityNotification(
          activityTitle,
          activityId,
          delayMinutes,
        );
      }

      // Note: Push notifications are typically sent from your backend server
      // This is for reference - you would send push notifications via your server
      if (type === "push" || type === "both") {
      }
    } catch (error) {
      crashlytics.logError(
        `Error in scheduleNotificationForActivity: ${JSON.stringify(error?.message)}`,
        "notificationManager.ts",
        error,
      );
    }
  }

  private static async scheduleLocalActivityNotification(
    activityTitle: string,
    activityId: string,
    delayMinutes: number = 0,
  ) {
    if (!Capacitor.isNativePlatform()) return;

    try {
      const notificationId =
        parseInt(activityId.slice(-6), 16) || Math.floor(Math.random() * 10000);

      await LocalNotifications.schedule({
        notifications: [
          {
            title: "New Activity Available!",
            body: `${activityTitle} - Join now!`,
            id: notificationId,
            schedule:
              delayMinutes > 0
                ? { at: new Date(Date.now() + delayMinutes * 60000) }
                : undefined,
            sound: "beep.wav",
            actionTypeId: "activity",
            extra: {
              activityId: activityId,
              type: "activity_notification",
            },
          },
        ],
      });
    } catch (error) {
      crashlytics.logError(
        `Error in scheduleLocalActivityNotification: ${JSON.stringify(error?.message)}`,
        "notificationManager.ts",
        error,
      );
    }
  }

  /**
   * Schedule notification for in-app notifications (messages, follows, etc.)
   */
  static async scheduleInAppNotification(
    type: "message" | "follow" | "general",
    title: string,
    body: string,
    data?: any,
  ) {
    if (!Capacitor.isNativePlatform()) return;

    try {
      const notificationId = Math.floor(Math.random() * 10000);
      // alert(`scheduling ${type} notification with id ${notificationId}`);
      await LocalNotifications.schedule({
        notifications: [
          {
            title,
            body,
            id: notificationId,
            sound: "beep.wav",
            actionTypeId: type,
            extra: {
              type: `${type}_notification`,
              ...data,
            },
          },
        ],
      });
    } catch (error) {
      crashlytics.logError(
        `Error in scheduleInAppNotification: ${JSON.stringify(error?.message)}`,
        "notificationManager.ts",
        error,
      );
    }
  }

  /**
   * Get notification status including push notification status
   */
  static async getFullNotificationStatus() {
    const localStatus = await this.getNotificationStatus();
    const pushStatus = await PushNotificationManager.getStatus();

    return {
      local: localStatus,
      push: pushStatus,
      fcmToken: PushNotificationManager.getToken(),
    };
  }

  static async setupNotificationHandlers(userId?: string) {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      // Set up local notification handlers
      await this.setupLocalNotificationHandlers();

      // Push notification handlers are set up automatically in PushNotificationManager.initialize()
      // but we can check their status here
      const pushStatus = await PushNotificationManager.getStatus();
    } catch (error) {
      crashlytics.logError(
        `Error in setupNotificationHandlers: ${JSON.stringify(error?.message)}`,
        "notificationManager.ts",
        error,
      );
    }
  }

  private static async setupLocalNotificationHandlers() {
    try {
      // Check if LocalNotifications is available and has addListener method
      if (
        !LocalNotifications ||
        typeof LocalNotifications.addListener !== "function"
      ) {
        crashlytics.logError(
          "LocalNotifications.addListener not available",
          "notificationManager.ts",
          new Error("LocalNotifications.addListener not available"),
        );
        return;
      }

      // Shared navigation helper - consistent with push notifications
      const navigateToPath = (path: string) => {
        // Store navigation intent in sessionStorage for app to pick up after auth
        try {
          sessionStorage.setItem(
            "pendingNotificationNavigation",
            JSON.stringify({
              path,
              timestamp: Date.now(),
              source: "local-notification",
            }),
          );
        } catch (e) {
          console.warn("Failed to store navigation intent:", e);
        }

        // Add delay to ensure Firebase Auth has time to restore session (critical for iOS)
        setTimeout(() => {
          try {
            // Method 1: Trigger React Router navigation by updating URL and dispatching event
            window.history.pushState({}, "", path);

            // Dispatch both popstate and a custom navigation event
            window.dispatchEvent(new PopStateEvent("popstate"));

            // Also dispatch a custom event that the app can listen to
            window.dispatchEvent(
              new CustomEvent("notification-navigate", {
                detail: { path, source: "local-notification" },
              }),
            );
          } catch (error) {
            console.warn(
              "⚠️ React Router navigation failed, using location fallback:",
              error,
            );
            // Method 2: Fallback to location change with hash to force navigation
            window.location.href = path + "#from-notification";
          }
        }, 1500); // Increased delay to allow auth restoration on iOS
      };

      // Handle local notification taps
      await LocalNotifications.addListener(
        "localNotificationActionPerformed",
        (notification) => {
          // Handle different notification types
          const extra = notification.notification.extra;
          const type = extra?.type;
          const { activityId, chatId, userId, link } = extra || {};

          // Enhanced navigation based on notification type - consistent with push notifications
          switch (type) {
            case "group_message":
              if (chatId) {
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest&type=group`,
                );
              } else if (activityId) {
                navigateToPath(`/activity/${activityId}?section=chat`);
              }
              break;

            case "direct_message":
              if (userId) {
                // navigateToDirectChat(userId);
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest&type=direct`,
                );
              } else if (chatId) {
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest&type=direct`,
                );
              }
              break;

            case "activity_member":
            case "activity_leave":
            case "activity_full":
              if (activityId) {
                navigateToPath(`/activity/${activityId}?section=participants`);
              }
              break;

            case "activity_update":
              if (activityId) {
                navigateToPath(`/activity/${activityId}?section=details`);
              }
              break;

            case "activity_cancelled":
            case "activity_kick":
              navigateToPath("/profile?tab=declined");
              break;

            case "follow_request":
              navigateToPath("/notifications?type=follow_requests");
              break;

            case "follow_accept":
            case "mutual_match":
              if (userId) {
                navigateToPath(`/profile/${userId}`);
              }
              break;

            case "story":
              if (userId) {
                navigateToPath(`/profile/${userId}?section=stories`);
              }
              break;

            case "direct_chat_start":
              if (chatId) {
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest&type=direct`,
                );
              } else if (userId) {
                // navigateToDirectChat(userId);
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest&type=direct`,
                );
              }
              break;

            case "activity_reminder":
              if (activityId) {
                navigateToPath(`/activity/${activityId}`);
              }
              break;

            case "follower_removed":
              navigateToPath("/notifications");
              break;

            case "account_deletion":
              navigateToPath("/notifications");
              break;

            // Legacy support for old notification types with '_notification' suffix
            case "activity_notification":
            case "activity":
              if (activityId) {
                navigateToPath(`/activity/${activityId}`);
              }
              break;

            case "message_notification":
            case "message":
              // Determine if it's group or direct based on data context
              const isGroupChat =
                extra?.isGroupChat || type === "group_message";
              if (isGroupChat && chatId) {
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest&type=group`,
                );
              } else if (userId) {
                // navigateToDirectChat(userId);
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest`,
                );
              } else if (chatId) {
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest`,
                );
              } else if (link) {
                navigateToPath(link);
              }
              break;

            case "group_message_notification":
              if (chatId) {
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest&type=group`,
                );
              } else if (activityId) {
                navigateToPath(`/activity/${activityId}?section=chat`);
              }
              break;

            case "activity_member_notification":
            case "activity_leave_notification":
            case "activity_full_notification":
              if (activityId) {
                navigateToPath(`/activity/${activityId}?section=participants`);
              }
              break;

            case "activity_update_notification":
              if (activityId) {
                navigateToPath(`/activity/${activityId}?section=details`);
              }
              break;

            case "follow_notification":
            case "follow":
              if (userId) {
                navigateToPath(`/profile/${userId}`);
              } else {
                navigateToPath("/notifications");
              }
              break;

            case "follow_accept_notification":
            case "mutual_match_notification":
              if (userId) {
                navigateToPath(`/profile/${userId}`);
              }
              break;

            case "story_notification":
              if (userId) {
                navigateToPath(`/profile/${userId}?section=stories`);
              }
              break;

            case "activity_cancelled_notification":
            case "activity_kick_notification":
              navigateToPath("/profile?tab=declined");
              break;

            case "direct_chat_notification":
              if (chatId) {
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest&type=direct`,
                );
              } else if (userId) {
                navigateToPath(
                  `/chat/${chatId}?from=notification&scroll=latest&type=direct`,
                );
                // navigateToDirectChat(userId);
              }
              break;

            default:
              if (link) {
                navigateToPath(link);
              } else {
                navigateToPath("/notifications");
              }
              break;
          }
        },
      );

      // Handle local notification received while app is open
      await LocalNotifications.addListener(
        "localNotificationReceived",
        (notification) => {},
      );
    } catch (error) {
      crashlytics.logError(
        `Error in initializeLocalNotifications: ${JSON.stringify(error?.message)}`,
        "notificationManager.ts",
        error,
      );
    }
  }

  static async cancelAllNotifications() {
    if (!Capacitor.isNativePlatform()) return;

    try {
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel(pending);
      }
    } catch (error) {
      crashlytics.logError(
        `Error in cancelAllNotifications: ${JSON.stringify(error?.message)}`,
        "notificationManager.ts",
        error,
      );
    }
  }

  static async getNotificationStatus() {
    if (!Capacitor.isNativePlatform()) {
      return { platform: "web", permissions: "N/A", pending: 0 };
    }

    try {
      const permissions = await LocalNotifications.checkPermissions();
      const pending = await LocalNotifications.getPending();

      return {
        platform: "native",
        permissions: permissions.display,
        pending: pending.notifications.length,
        pendingIds: pending.notifications.map((n) => n.id),
      };
    } catch (error) {
      crashlytics.logError(
        `Error in getNotificationStatus: ${JSON.stringify(error?.message)}`,
        "notificationManager.ts",
        error,
      );
      return { platform: "native", permissions: "error", pending: 0 };
    }
  }
}
